import { CallMode, CallsService, Language, type OrderItem } from '@shared/openapi/delivery-api';
import { Room, RoomEvent, Track } from 'livekit-client';
import { ref } from 'vue';

export default () => {
  const callId = ref<string | null>(null);
  const status = ref<string | null>(null);
  const items = ref<OrderItem[]>([]);
  const error = ref<string | null>(null);
  let pollHandle: ReturnType<typeof setInterval> | undefined;
  let room: Room | undefined;

  const stopPolling = () => {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = undefined;
    }
  };

  const disconnectRoom = () => {
    room?.disconnect();
    room = undefined;
  };

  const poll = () => {
    stopPolling();
    pollHandle = setInterval(async () => {
      if (!callId.value) return;
      try {
        const response = await CallsService.getCallV1CallsCallIdGet({
          path: { call_id: callId.value }
        });
        status.value = response.data?.status ?? null;
        items.value = response.data?.items ?? [];
        if (status.value === 'completed' || status.value === 'failed') {
          stopPolling();
          disconnectRoom();
        }
      } catch (e) {
        console.error('Error polling call status:', e);
      }
    }, 3000);
  };

  const connectBrowserCall = async (livekitUrl: string, token: string) => {
    const newRoom = new Room();
    newRoom.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        document.body.appendChild(track.attach());
      }
    });
    newRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => el.remove());
    });
    try {
      await newRoom.connect(livekitUrl, token);
      await newRoom.localParticipant.setMicrophoneEnabled(true);
    } catch (e) {
      newRoom.disconnect();
      throw e;
    }
    room = newRoom;
  };

  const startCall = async (mode: CallMode, phoneNumber?: string, language?: Language) => {
    error.value = null;
    items.value = [];
    try {
      const response = await CallsService.startCallV1CallsPost({
        body:
          mode === CallMode.PHONE
            ? { mode, phone_number: phoneNumber, language }
            : { mode, language }
      });
      callId.value = response.data?.call_id ?? null;
      status.value = response.data?.status ?? null;

      const { livekit_url: livekitUrl, livekit_token: token } = response.data ?? {};
      if (mode === CallMode.BROWSER && livekitUrl && token) {
        try {
          await connectBrowserCall(livekitUrl, token);
        } catch (e) {
          console.error('Error connecting to browser call:', e);
          error.value = 'Could not access the microphone.';
        }
      }

      poll();
    } catch (e: any) {
      error.value = e?.response?.data?.detail ?? 'Could not start the call.';
    }
  };

  return { state: { callId, status, items, error }, funcs: { startCall }, consts: {} };
};
